import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { BellRinging, CheckCircle, Clock, WarningCircle } from '@phosphor-icons/react';
import { API } from '../config/api';
import { Switch } from './ui/switch';
import { Card, CardContent } from './ui/card';

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '');

/** Quote page: state of the automatic follow-up emails to the client, with an on/off switch. */
export default function QuoteFollowupsCard({ quoteId, status }) {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/quotes/${quoteId}/client-followups`).then(r => setData(r.data)).catch(() => setData(null));
  }, [quoteId, status]);

  if (!data) return null;

  const toggle = async (enabled) => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/quotes/${quoteId}/client-followups`, { enabled });
      setData(r.data);
      toast.success(enabled ? 'Solleciti automatici attivati' : 'Solleciti automatici disattivati per questo preventivo');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore');
    } finally {
      setSaving(false);
    }
  };

  const neverSent = !data.sent_at;
  return (
    <Card className="border border-[#E2E8F0] rounded-sm" data-testid="quote-followups-card">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <BellRinging size={20} className="text-[#0033A0] shrink-0" />
            <span className="font-outfit text-sm font-semibold text-[#0A0F1A]">Solleciti automatici al cliente</span>
          </div>
          <Switch
            checked={data.enabled !== false}
            onCheckedChange={toggle}
            disabled={saving}
            aria-label="Solleciti automatici per questo preventivo"
            data-testid="quote-followups-switch"
          />
        </div>
        {neverSent ? (
          <p className="text-sm text-[#4A5568]">
            Partono dopo l'invio del preventivo per email dall'app (pulsante "Invia via Email" nell'elenco preventivi).
          </p>
        ) : (
          <>
            <p className="text-sm text-[#4A5568]">Inviato a <strong className="text-[#0A0F1A]">{data.sent_to_email}</strong> il {fmt(data.sent_at)}</p>
            <ul className="space-y-1.5">
              {data.steps.map(s => (
                <li key={s.step} className="flex items-center gap-2 text-sm" data-testid={`quote-followup-step-${s.step}`}>
                  {s.status === 'sent' && <CheckCircle size={16} className="text-green-600" weight="fill" />}
                  {s.status === 'scheduled' && <Clock size={16} className="text-[#0033A0]" />}
                  {s.status === 'failed' && <WarningCircle size={16} className="text-red-600" weight="fill" />}
                  <span className="text-[#0A0F1A]">{s.step}° sollecito</span>
                  <span className="text-[#4A5568]">
                    {s.status === 'sent' && `inviato il ${fmt(s.sent_at)}`}
                    {s.status === 'scheduled' && (data.stop_reason ? 'non verrà inviato' : `previsto dal ${fmt(s.due)}`)}
                    {s.status === 'failed' && 'invio non riuscito: controlla la configurazione email'}
                  </span>
                </li>
              ))}
            </ul>
          </>
        )}
        {data.stop_reason && !neverSent && (
          <p className="text-xs text-[#4A5568] bg-[#F4F6F8] rounded-sm px-2 py-1.5">{data.stop_reason}</p>
        )}
      </CardContent>
    </Card>
  );
}
